import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail, deleteUser, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { doc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const DeleteAccountPage = () => {
    const [showResetModal, setShowResetModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [resetEmail, setResetEmail] = useState('');
    const [resetMsg, setResetMsg] = useState('');
    const [resetErr, setResetErr] = useState('');

    const [deleteEmail, setDeleteEmail] = useState('');
    const [deletePass, setDeletePass] = useState('');
    const [deleteMsg, setDeleteMsg] = useState('');
    const [deleteErr, setDeleteErr] = useState('');
    const [processing, setProcessing] = useState(false);

    const handleSendReset = async (e) => {
        e.preventDefault();
        setResetMsg('');
        setResetErr('');
        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setResetMsg("Se ha enviado un correo con instrucciones para restablecer tu contraseña.");
        } catch (err) {
            setResetErr(err.message || "Error al enviar correo de recuperación.");
        }
    };

    const handleDeleteAccount = async (e) => {
        e.preventDefault();
        setDeleteMsg('');
        setDeleteErr('');
        setProcessing(true);

        try {
            const currentUser = auth.currentUser;
            if (!currentUser) {
                setDeleteErr("Debes haber iniciado sesión previamente para eliminar tu cuenta.");
                setProcessing(false);
                return;
            }

            // Re-authenticate user before deleting
            const credential = EmailAuthProvider.credential(deleteEmail, deletePass);
            await reauthenticateWithCredential(currentUser, credential);

            // Clean up Firestore profile document
            await deleteDoc(doc(db, `Miembros/${currentUser.uid}`)).catch(() => {});

            // Delete Firebase auth user
            await deleteUser(currentUser);

            setDeleteMsg("Tu cuenta ha sido eliminada permanentemente.");
            setTimeout(() => {
                window.location.href = "/";
            }, 3000);
        } catch (err) {
            setDeleteErr(err.message || "Error al eliminar la cuenta. Verifica tus credenciales.");
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="bg-gray-50 min-vh-100 font-sans d-flex flex-column">
            {/* Header */}
            <header className="py-16 bg-white border-bottom shadow-2xs">
                <div className="container container-lg d-flex align-items-center justify-content-between">
                    <Link to="/" className="d-flex align-items-center gap-12 text-decoration-none">
                        <img src="/logo192.png" alt="Catálogo App" className="w-36 h-36 object-fit-contain" />
                        <span className="fw-bold text-gray-900 text-lg">Catálogo App</span>
                    </Link>
                    <Link to="/admin/login" className="btn btn-outline-main rounded-pill px-20 py-6 text-xs fw-semibold">
                        Acceso Vendedores
                    </Link>
                </div>
            </header>

            {/* Main Content */}
            <div className="container container-lg py-60 flex-grow-1" style={{ maxWidth: '720px' }}>
                <div className="bg-white p-32 p-md-40 rounded-24 border shadow-sm text-center mb-32">
                    <div className="w-64 h-64 rounded-circle bg-danger-100 text-danger-600 flex-center mx-auto mb-20">
                        <i className="ph ph-warning text-3xl"></i>
                    </div>
                    <h3 className="fw-bold text-gray-900 mb-12">Eliminar Cuenta o Restablecer Contraseña</h3>
                    <p className="text-gray-600 text-sm mb-32 lh-lg max-w-500 mx-auto">
                        Al eliminar tu cuenta de <strong>Catálogo App</strong>, perderás acceso permanentemente a tus catálogos, productos e imágenes configuradas.
                    </p>

                    <div className="d-flex align-items-center justify-content-center gap-16 flex-wrap">
                        <button 
                            type="button" 
                            className="btn btn-outline-main rounded-pill px-24 py-12 text-xs fw-bold flex-align gap-8"
                            onClick={() => setShowResetModal(true)}
                        >
                            <i className="ph ph-key text-sm"></i>
                            Restablecer Contraseña
                        </button>

                        <button 
                            type="button" 
                            className="btn btn-danger rounded-pill px-24 py-12 text-xs fw-bold flex-align gap-8 shadow-xs"
                            onClick={() => setShowDeleteModal(true)}
                        >
                            <i className="ph ph-trash text-sm"></i>
                            Eliminar mi Cuenta Definitivamente
                        </button>
                    </div>
                </div>
            </div>

            {/* Reset Password Modal */}
            {showResetModal && (
                <div className="modal fade show d-block bg-black-50 z-5" tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '440px' }}>
                        <div className="modal-content rounded-24 border-0 shadow-lg overflow-hidden">
                            <div className="modal-header border-bottom p-20 bg-gray-50 flex-between">
                                <h6 className="modal-title fw-bold text-gray-900 mb-0">Restablecer Contraseña</h6>
                                <button type="button" className="btn-close" onClick={() => setShowResetModal(false)}></button>
                            </div>
                            <form onSubmit={handleSendReset}>
                                <div className="modal-body p-24">
                                    {resetMsg && <div className="alert alert-success text-xs py-10 px-14 mb-16 rounded-12">{resetMsg}</div>}
                                    {resetErr && <div className="alert alert-danger text-xs py-10 px-14 mb-16 rounded-12">{resetErr}</div>}
                                    <div className="mb-16">
                                        <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Correo Electrónico</label>
                                        <input 
                                            type="email" 
                                            className="form-control rounded-12 py-10 px-16 text-sm"
                                            placeholder="tu@email.com"
                                            value={resetEmail}
                                            onChange={(e) => setResetEmail(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer p-16 border-top bg-gray-50 flex-between">
                                    <button type="button" className="btn btn-outline-gray-200 rounded-pill px-20 text-xs fw-semibold" onClick={() => setShowResetModal(false)}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn btn-main rounded-pill px-24 py-10 text-xs fw-bold">
                                        Enviar Enlace
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Account Modal */}
            {showDeleteModal && (
                <div className="modal fade show d-block bg-black-50 z-5" tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '440px' }}>
                        <div className="modal-content rounded-24 border-0 shadow-lg overflow-hidden">
                            <div className="modal-header border-bottom p-20 bg-gray-50 flex-between">
                                <h6 className="modal-title fw-bold text-danger mb-0">Confirmar Eliminación de Cuenta</h6>
                                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
                            </div>
                            <form onSubmit={handleDeleteAccount}>
                                <div className="modal-body p-24">
                                    {deleteMsg && <div className="alert alert-success text-xs py-10 px-14 mb-16 rounded-12">{deleteMsg}</div>}
                                    {deleteErr && <div className="alert alert-danger text-xs py-10 px-14 mb-16 rounded-12">{deleteErr}</div>}
                                    <p className="text-xs text-gray-600 mb-16">
                                        Por motivos de seguridad, ingresa tu correo y contraseña para confirmar la eliminación permanente de tu cuenta.
                                    </p>
                                    <div className="mb-16">
                                        <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Correo Registrado</label>
                                        <input 
                                            type="email" 
                                            className="form-control rounded-12 py-10 px-16 text-sm"
                                            placeholder="tu@email.com"
                                            value={deleteEmail}
                                            onChange={(e) => setDeleteEmail(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="mb-16">
                                        <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Contraseña Actual</label>
                                        <input 
                                            type="password" 
                                            className="form-control rounded-12 py-10 px-16 text-sm"
                                            placeholder="••••••••"
                                            value={deletePass}
                                            onChange={(e) => setDeletePass(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer p-16 border-top bg-gray-50 flex-between">
                                    <button type="button" className="btn btn-outline-gray-200 rounded-pill px-20 text-xs fw-semibold" onClick={() => setShowDeleteModal(false)}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn btn-danger rounded-pill px-24 py-10 text-xs fw-bold" disabled={processing}>
                                        {processing ? 'Eliminando...' : 'Eliminar Cuenta'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeleteAccountPage;
